import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import PageMeta from "@/components/seo/PageMeta";
import Button from "@/components/ui/Button";
import ScriptText from "@/components/ui/ScriptText";
import { unsubscribeContent } from "@/data/content";
import { useContent } from "@/hooks/useContent";

type Status = "loading" | "success" | "error";

export default function UnsubscribePage() {
  const content = useContent("pages/unsubscribe", unsubscribeContent);
  const [searchParams] = useSearchParams();
  const success = searchParams.get("success") === "true";
  const token = searchParams.get("token");

  const [status, setStatus] = useState<Status>(success ? "success" : "loading");

  useEffect(() => {
    if (success || !token) {
      if (!success) setStatus("error");
      return;
    }

    fetch("/api/unsubscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then((res) => {
        setStatus(res.ok ? "success" : "error");
      })
      .catch(() => setStatus("error"));
  }, [success, token]);

  return (
    <>
      <PageMeta
        title="Unsubscribe — Chasing Provence"
        description="Manage your email subscription preferences for Chasing Provence."
        noindex
      />

      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="text-center">
          {status === "loading" && (
            <>
              <ScriptText className="mb-4 block text-5xl">{content.loadingScript}</ScriptText>
              <p className="mx-auto mb-10 max-w-md font-body leading-relaxed text-soft-charcoal/70">
                {content.loadingBody}
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <ScriptText className="mb-4 block text-5xl">{content.successScript}</ScriptText>
              <h1 className="mb-4 font-serif text-3xl font-light text-soft-charcoal lg:text-4xl">
                {content.successHeading}
              </h1>
              <p className="mx-auto mb-10 max-w-md font-body leading-relaxed text-soft-charcoal/70">
                {content.successBody}
              </p>
              <Button to={content.ctaLink}>{content.ctaText}</Button>
            </>
          )}

          {status === "error" && (
            <>
              <ScriptText className="mb-4 block text-5xl">{content.errorScript}</ScriptText>
              <h1 className="mb-4 font-serif text-3xl font-light text-soft-charcoal lg:text-4xl">
                {content.errorHeading}
              </h1>
              <p className="mx-auto mb-10 max-w-md font-body leading-relaxed text-soft-charcoal/70">
                {content.errorBody}
              </p>
              <Button to={content.ctaLink}>{content.ctaText}</Button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
